<template>
  <div>
    <div v-if="!pageLoading">
      <top-card  current="CoverChange" :premium-amount="renewal.premiumAfterTax" :cover-type="coverType" :free-benefits="renewal.freeBenefits">Please confirm that the information below is accurate before you proceed to pay</top-card>
      <div class="tw-flex tw-justify-center tw-justify-items-center tw-my-8">
        <back-nav to="VehicleDetailsReview" />
      </div>
      <v-card>
        <div class="tw-w-full tw-p-10">
          <PremiumAmount :premium-amount="renewal.premiumAfterTax">Total Premium</PremiumAmount>
          <div class="tw-w-full tw-flex tw-justify-center justify-items-center">
            <v-btn
              v-if="!hideTpoChangePlan"
              class="c-light-blue-text c-title tw-p-5 tw-bg-transparent tw-m-5 tw-rounded-none tw-normal-case"
              color="primary"
              outlined
              @click="changeCover"
            >Change Plan to {{ this.coverType.coverTypeId === 120 ? 'Third Party Only' : 'Comprehensive' }}</v-btn>
          </div>
          <div
            v-if="hideTpoChangePlan"
            class="tw-w-full tw-mb-4 tw-p-4 tw-rounded-lg tw-bg-orange-100 tw-text-blue-600"
          >
            {{ tpoDisabledMessage }}
          </div>
          <div v-if="this.coverType.coverTypeId === 120">
            <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-px-2 md:tw-px-8 tw-mt-3 md:tw-mt-10">Chargeable Benefits</h3>
            <hr class="tw-mt-3 tw-mb-5">
            <div class="tw-grid md:tw-grid-rows-2 tw-grid-flow-row md:tw-grid-flow-col tw-gap-3 tw-px-10 tw-pt-5">
            <div
              class="tw-border tw-rounded-md tw-shadow-md tw-p-3">
              <v-checkbox
                  class="title-font c-blue-text"
                  v-model="includeExcessProtector"
                  label="Excess Protector Benefits"
                  @change="() => {
                    if (!this.includeExcessProtector) {
                      this.changeBenefit([{key: 'excessProtectorFull', value: false}, {key: 'excessProtectorOwn', value:false}])
                      this.excessProtectorKey = '';
                    }
                  }"
                  :disabled="!includeExcessProtector"
              ></v-checkbox>
              <hr class="tw-my-3"/>
              <v-radio-group
                  v-model="excessProtectorKey"
                  @change="() => {
                    this.includeExcessProtector = !!this.excessProtectorKey;
                    this.changeBenefit([{key: this.excessProtectorKey, value: true }, {
                      key: this.excessProtectorKey === 'excessProtectorFull'?'excessProtectorOwn':'excessProtectorFull',
                      value: false
                    }]);
                  }"
              >
                <v-radio
                    color="primary"
                    v-for="(benefit, i) in excessProtectorBenefits()"
                    :key="i"
                    :label="benefit.sectDesc"
                    :value="benefit.key"
                ></v-radio>
              </v-radio-group>
            </div>
            <div
              class="tw-border tw-rounded-md tw-shadow-md tw-p-3">
              <v-checkbox
                  class="title-font c-blue-text"
                  v-model="includeCourtesyCar"
                  label="Courtesy Car Benefits"
                  @change="() => {
                    if (!this.includeCourtesyCar) {
                      this.changeBenefit([{key: 'courtesyCar10', value: false},{key: 'courtesyCar20', value: false}])
                      this.courtesyCarKey = '';
                    }
                  }"
                  :disabled="!includeCourtesyCar"
              ></v-checkbox>
              <hr class="tw-my-3"/>
              <v-radio-group
                  v-model="courtesyCarKey"
                  @change="() => {
                    this.includeCourtesyCar = !!this.courtesyCarKey;
                    this.changeBenefit([{key: this.courtesyCarKey, value: true},
                    {
                      key: this.courtesyCarKey === 'courtesyCar10'?'courtesyCar20':'courtesyCar10',
                      value: false
                    }]);
                  }"
              >
                <v-radio
                    color="primary"
                    v-for="(benefit, i) in courtesyCarBenefits()"
                    :key="i"
                    :label="benefit.sectDesc"
                    :value="benefit.key"
                ></v-radio>
              </v-radio-group>
            </div>
            <v-checkbox v-for="(benefit, index) in independentBenefits()"
              class="tw-font-bold tw-flex tw-items-center tw-justify-center tw-text-center tw-shadown-md"
              :key="index"
              :label="benefit.sectDesc"
              :input-value="benefit.isIncludedInPremium"
              @change="(value) => {
                benefit.isIncludedInPremium = value;
                changeBenefit([{key: benefit.key,value: value}]);
              }"
            ></v-checkbox>
          </div>
          </div>
          <div class="tw-w-full tw-flex tw-justify-center tw-mt-10 tw-text-md">
            <v-btn class="tw-rounded-none tw-normal-case c-title" @click="proceed" :disabled="pageLoading" color="primary">
              {{ pageLoading ? 'Updating...' : 'Select Payment Plan' }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>
    <div v-else class="tw-w-full tw-flex tw-justify-center">
      <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import TopCard from '@/components/renew/TopCard';
import BackNav from '@/components/buy/BackNav';
import helpers from '@/utils/helpers';
import moment from 'moment';
import { FB_BUY_COVER_SUMMARY } from '@/utils/const';
import PremiumAmount from '@/components/buy/PremiumAmount';
import renewalMixin from '@/mixins/renewalMixin';
import unleashMixin from '@/mixins/unleashMixin';
import axios from 'axios';

const TPO_GATE_FEATURE_FLAG = 'TPO_BUSINESS';
const TPO_DISABLED_MESSAGE = 'Important Notice: We no longer support the purchase of Third Party Only (TPO) motor insurance policies through our digital channels. For assistance with your insurance requirement, please contact our Customer Service team at customer.service@apollo.co.ke';

export default {
  name: 'CoverSummaryReview',
  props: ['renewal'],
  components: {
    BackNav,
    TopCard,
    PremiumAmount,
  },
  data() {
    return {
      loading: false,
      pageLoading: true,
      checked: true,
      benefits: {
        aa: false,
        travel: false,
        violence: true,
        courtesyCar10: false,
        courtesyCar20: false,
        excessProtectorFull: false,
        excessProtectorOwn: true,
      },
      excessProtectorKey: this.excessProtectorBenefits().filter((benefit) => benefit.isIncludedInPremium).length > 0
        ? this.excessProtectorBenefits().filter((benefit) => benefit.isIncludedInPremium)[0].key : '',
      includeExcessProtector: this.excessProtectorBenefits().filter((benefit) => benefit.isIncludedInPremium).length > 0,
      includeCourtesyCar: this.courtesyCarBenefits().filter((benefit) => benefit.isIncludedInPremium).length > 0,
      courtesyCarKey: this.excessProtectorBenefits().filter((benefit) => benefit.isIncludedInPremium).length > 0
        ? this.excessProtectorBenefits().filter((benefit) => benefit.isIncludedInPremium)[0].key : '',
      beneficiaries2: [],
    };
  },
  computed: {
    quote() {
      return this.$store.getters['renewal/fetchQoute'];
    },

    instalments() {
      return this.renewal.premiumInstalments;
    },
    user() {
      return this.quote.client;
    },
    car() {
      return this.coverType.car;
    },
    disableTpoRenewal() {
      return !this.isFlagEnabled(TPO_GATE_FEATURE_FLAG);
    },
    hideTpoChangePlan() {
      return this.disableTpoRenewal && this.coverType.coverTypeId === 120;
    },
    tpoDisabledMessage() {
      return TPO_DISABLED_MESSAGE;
    },
  },
  metaInfo: {
    title: 'Renew - Cover Details Summary',
  },
  mixins: [renewalMixin, unleashMixin],
  mounted() {
    this.benefits.aa = this.getChargeableBenefit('aa').isIncludedInPremium;
    this.benefits.travel = this.getChargeableBenefit('travel').isIncludedInPremium;
    this.benefits.violence = this.getChargeableBenefit('violence').isIncludedInPremium;
    this.benefits.excessProtectorFull = this.getChargeableBenefit('excessProtectorFull').isIncludedInPremium;
    this.benefits.excessProtectorOwn = this.getChargeableBenefit('excessProtectorOwn').isIncludedInPremium;
    this.benefits.courtesyCar10 = this.getChargeableBenefit('courtesyCar10').isIncludedInPremium;
    this.benefits.courtesyCar20 = this.getChargeableBenefit('courtesyCar20').isIncludedInPremium;
    this.$store.dispatch('renewal/fetchQuotation', { batchNo: this.$route.params.batchNo, quoteId: this.renewal.quoteId }).then(() => {
      this.pageLoading2 = false;
    });
    if (this.coverType.covertypeId === 120 && 'additionalBenefits' in this.coverType && 'beneficiaries' in this.coverType) {
      this.coverType.beneficiaries.forEach((b) => {
        this.beneficiaries2.push({
          fullName: b.fullName,
          passportNumber: b.passportNumber,
          dateOfBirth: b.dateOfBirth,
          relationship: b.relationship,
        });
      });
    }
    this.changeBenefit([
      {
        key: 'aa',
        value: this.benefits.aa,
      },
      {
        key: 'violence',
        value: this.benefits.violence,
      },
      {
        key: 'travel',
        value: this.benefits.travel,
      },
      {
        key: 'excessProtectorOwn',
        value: this.benefits.excessProtectorOwn,
      },
      {
        key: 'excessProtectorFull',
        value: this.benefits.excessProtectorFull,
      },
      {
        key: 'courtesyCar10',
        value: this.benefits.courtesyCar10,
      },
      {
        key: 'courtesyCar20',
        value: this.benefits.courtesyCar20,
      },
    ]);
  },
  filters: {
    formatNum(num) {
      return helpers.numberToString(num);
    },
    formatDate(date) {
      return moment(date, 'YYYY-MM-DD').format('Do MMM YYYY');
    },
  },
  methods: {
    proceed() {
      if (this.disableTpoRenewal && this.coverType.coverTypeId === 121) {
        this.$toast.error(TPO_DISABLED_MESSAGE);
        return;
      }
      if (!this.pageLoading) {
        this.$analytics.logEvent(FB_BUY_COVER_SUMMARY);
        if (this.coverType.coverTypeId === 121) {
          this.$emit('to-branch-selection-review', this.renewal);
        } else {
          this.$emit('to-payment-selection', this.renewal);
        }
      }
    },

    changeCover() {
      if (this.disableTpoRenewal && this.coverType.coverTypeId === 120) {
        this.$toast.error(TPO_DISABLED_MESSAGE);
        return;
      }
      this.pageLoading = true;
      const payload = JSON.parse(
        localStorage.getItem('renewalData') || '{}',
      );
      payload.policy.coverType = this.coverType.coverTypeId === 120 ? 'Third Party' : 'Comprehensive';
      axios
        .post('/motor/api/policy/renewal', payload)
        .then((response) => {
          this.renewal = response.data.data;
          this.coverType.name = response.data.data.coverType;
          this.coverType.coverTypeId = this.coverType.name === 'Comprehensive' ? 120 : 121;
          this.pageLoading = false;
        });
    },
    getChargeableBenefit(key) {
      return this.coverType.chargeableBenefits.find((benefit) => benefit.key === key);
    },
    excessProtectorBenefits() {
      return [this.getChargeableBenefit('excessProtectorOwn'), this.getChargeableBenefit('excessProtectorFull')];
    },
    courtesyCarBenefits() {
      return [this.getChargeableBenefit('courtesyCar10'), this.getChargeableBenefit('courtesyCar20')];
    },
    independentBenefits() {
      return this.coverType.chargeableBenefits.filter((benefit) => {
        const key = benefit.key;
        return key !== 'excessProtectorOwn' && key !== 'excessProtectorFull' && key !== 'courtesyCar10' && key !== 'courtesyCar20';
      });
    },
    changeBenefit(benefits) {
      this.pageLoading = true;
      benefits.forEach((benefit) => {
        const key = benefit.key;
        const value = benefit.value;

        switch (key) {
          case 'aa':
            this.benefits.aa = value;
            break;
          case 'travel':
            this.benefits.travel = value;
            break;
          case 'violence':
            this.benefits.violence = value;
            break;
          case 'courtesyCar10':
            this.benefits.courtesyCar10 = value;
            break;
          case 'courtesyCar20':
            this.benefits.courtesyCar20 = value;
            break;
          case 'excessProtectorFull':
            this.benefits.excessProtectorFull = value;
            break;
          case 'excessProtectorOwn':
            this.benefits.excessProtectorOwn = value;
            break;
          default:
            this.$toast.error(`Missed ${key} chargeable benefit.`);
            break;
        }
      });

      const payload = JSON.parse(
        localStorage.getItem('renewalData') || '{}',
      );
      payload.benefit = this.benefits;
      axios
        .post('/motor/api/policy/renewal', payload)
        .then((response) => {
          this.renewal = response.data.data;
          payload.premiumAfterTax = response.data.data.premiumAfterTax;
          payload.premiumInstalments = response.data.data.premiumInstalments || [];
          payload.freeBenefits = response.data.data.freeBenefits || [];
          localStorage.setItem('renewalData', JSON.stringify(payload));
          this.pageLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.list-none{
  list-style: none!important;
}
</style>
